import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getPost, type Post } from "../content/loadPosts";
import {
  absoluteContentUrl,
  createBlogPostingJsonLd,
  getLinkBehavior,
} from "../content/blogSeo";
import "./BlogPost.css";

const DEFAULT_DESCRIPTION =
  "ASDRY Beta turns weather and environmental data into practical preparation guidance for running, walking, commuting, cycling, dog walks, gardening, and outdoor plans.";

const getYouTubeId = (url: string | undefined | null) => {
  if (!url || typeof url !== "string") return null;
  const match = url.match(
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/
  );
  return match ? match[1] : null;
};

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    getPost(slug || "")
      .then((found) => setPost(found))
      .catch(() => {
        setPost(null);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!post?.description) return;
    let description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const created = !description;
    if (!description) {
      description = document.createElement("meta");
      description.name = "description";
      document.head.appendChild(description);
    }
    description.content = post.description;
    return () => {
      if (created) description?.remove();
      else if (description) description.content = DEFAULT_DESCRIPTION;
    };
  }, [post]);

  if (loading) {
    return (
      <div className="page">
        <div className="page-container">
          <p>Loading post…</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="not-found">
        <h1>{loadError ? "Unable to Load Post" : "Post Not Found"}</h1>
        <Link to="/blog">← Back to Blog</Link>
      </div>
    );
  }

  const resolveImageUrl = (src: string) => absoluteContentUrl(src) || src;
  const socialImage = absoluteContentUrl(post.image);
  const documentTitle = `${post.seoTitle || post.title} | ASDRY Blog`;

  return (
    <div className="page">
      <div className="page-container">
        <Helmet>
          <title>{documentTitle}</title>
          <link rel="canonical" href={post.canonicalUrl} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={documentTitle} />
          <meta property="og:description" content={post.description} />
          <meta property="og:url" content={post.canonicalUrl} />
          {socialImage && <meta property="og:image" content={socialImage} />}
          <meta name="twitter:card" content={socialImage ? "summary_large_image" : "summary"} />
          <meta name="twitter:title" content={documentTitle} />
          <meta name="twitter:description" content={post.description} />
          {socialImage && <meta name="twitter:image" content={socialImage} />}
          <script type="application/ld+json">
            {JSON.stringify(createBlogPostingJsonLd(post))}
          </script>
        </Helmet>

        <div className="content-card">
          <div className="card-header">
            <h1 className="content-title">{post.title}</h1>

            <div className="content-meta">
              <span className="post-author">{post.author || "Philip Jakeman"}</span>

              {post.date && (
                <>
                  <span className="post-meta-separator">·</span>
                  <time className="post-date" dateTime={post.date}>
                    {post.date}
                  </time>
                </>
              )}

              {post.readingTime && (
                <>
                  <span className="post-meta-separator">·</span>
                  <span className="post-reading-time">
                    {post.readingTime} min read
                  </span>
                </>
              )}
            </div>

            {post.image && (
              <img
                src={resolveImageUrl(post.image)}
                alt={post.title}
                className="content-hero"
              />
            )}
          </div>

          <div className="card-body">
            <ReactMarkdown
              components={{
                h1: ({ children }: any) => <h2>{children}</h2>,
                img: (props: any) => (
                  <img
                    {...props}
                    src={resolveImageUrl(props.src || "")}
                    alt={props.alt || ""}
                    className="content-image"
                  />
                ),
                a: ({ href, children }: any) => {
                  const videoId = getYouTubeId(href || "");
                  if (videoId) {
                    return (
                      <div className="content-video">
                        <iframe
                          src={`https://www.youtube.com/embed/${videoId}`}
                          title="YouTube video player"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    );
                  }
                  const linkBehavior = getLinkBehavior(href);
                  return (
                    <a
                      href={href}
                      {...(linkBehavior.external
                        ? { target: linkBehavior.target, rel: linkBehavior.rel }
                        : {})}
                      className="content-link"
                    >
                      {children}
                    </a>
                  );
                },
                p: ({ children }: any) => {
                  const text = Array.isArray(children) ? children.join(" ") : children;
                  if (
                    typeof text === "string" &&
                    text.match(/https?:\/\/(www\.)?(youtube\.com|youtu\.be)\//)
                  ) {
                    const videoId = getYouTubeId(text);
                    if (videoId) {
                      return (
                        <div className="content-video">
                          <iframe
                            src={`https://www.youtube.com/embed/${videoId}`}
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      );
                    }
                  }
                  return <p>{children}</p>;
                },
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          <div className="card-footer">
            <Link to="/blog" className="content-back">
              ← Back to Blog
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
